import type { ReactNode } from "react";
import { useActiveOrgName, useActiveTeamLeague, useActiveTeamName } from "../lib/use-active-org.js";
import { AppHeader } from "./app-header/index.js";

/**
 * Thin app shell — every non-home route renders inside this. Hosts the
 * unified `AppHeader` (back chevron + page title + glyph cluster) and gives
 * the page a single scroll container.
 *
 * No sidebar, no bottom-nav: navigation is launched from the home tile grid
 * and each page returns there via the back chevron.
 */
export function AppShell({
  title,
  backTo,
  wide,
  chromeless,
  children,
}: {
  title: string;
  backTo?: string;
  /** When true, drops the max-width cap so the page can use the whole viewport. */
  wide?: boolean;
  /** When true, hides the AppHeader entirely (e.g. for full-bleed editors). */
  chromeless?: boolean;
  children: ReactNode;
}) {
  const orgName = useActiveOrgName();
  const teamName = useActiveTeamName();
  const teamLeague = useActiveTeamLeague()?.trim() || null;
  // Sub-header shows the active context as "Verein / Team (Liga)" so the
  // user always sees which team a sub-page belongs to. Falls back
  // gracefully when either piece is missing.
  const teamLabel = teamName ? (teamLeague ? `${teamName} (${teamLeague})` : teamName) : null;
  const subtitle = [orgName, teamLabel].filter(Boolean).join(" / ") || undefined;

  // `wide` mode flips the shell into a full-height flex column so the page
  // can claim the remaining viewport (used by the Tactics editor for a
  // proper big-pitch experience). `chromeless` additionally drops the
  // AppHeader so the page can use the entire screen.
  //
  // Padding mirrors the home cockpit (`.home-stage` in home.css) so
  // navigation between home ⇄ child page is shift-free:
  //   • base (≤640):   12px / 16px / 12px       (safe-area aware)
  //   • sm  (≥641):    20px / 24px / 20px       (portrait tablet)
  //   • lg  (≥1024):   24px / 24px / 24px       (landscape)
  //   • 2xl (≥1536):   32px / 24px / 32px       (ultra-wide)
  // The max-width cap is removed so AppShell spans the full viewport just
  // like the home cockpit does.
  return (
    <div className={`bg-background ${wide ? "flex h-svh flex-col overflow-hidden" : "min-h-svh"}`}>
      <div
        className={`mx-auto flex w-full flex-col ${
          chromeless
            ? "gap-0 px-0 pt-0"
            : "gap-4 px-4 pt-[max(12px,env(safe-area-inset-top))] sm:px-6 sm:pt-[max(20px,env(safe-area-inset-top))] lg:pt-6 2xl:pt-8"
        } ${wide ? "flex-1 min-h-0" : ""}`}
      >
        {!chromeless && <AppHeader mode="page" title={title} subtitle={subtitle} backTo={backTo} />}
        <main
          className={`min-w-0 ${
            wide
              ? `flex flex-1 min-h-0 flex-col ${chromeless ? "pb-0" : "pb-4"}`
              : "pb-[max(12px,env(safe-area-inset-bottom))] sm:pb-[max(20px,env(safe-area-inset-bottom))] lg:pb-6 2xl:pb-8"
          }`}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
